import { isEmpty } from 'lodash';
import regionApi from '~/clientApis/regionApi';
import { showMessageModal } from '~/clientUtils/showMessageModal';
import { UpdateSettingsProps } from '~/definitions/generalSettings';
import { Region } from '~/definitions/region';
import { getStore } from '~/mobx/AppStore';

const updateRegions = async (selectedRegionData: Region) => {
    const { electionMenuStore, generalSettingsStore } = getStore();
    const { regionName } = generalSettingsStore;

    if (regionName === selectedRegionData?.name) {
        return null;
    }

    await electionMenuStore.getRegions().then(() => {
        const electionMenu = electionMenuStore.getElectionMenu();
        if (electionMenu) {
            electionMenuStore.setElectionMenu(electionMenu);
        }
    });
};

const assignToRegionSettingsData = async (params: { regionId: string; data: UpdateSettingsProps[] }) => {
    const { generalSettingsStore } = getStore();

    try {
        const result = await regionApi.assignToRegionSettings({ ...params });
        if (result.success) {
            generalSettingsStore.updateRegionAttributeData(params.data);
        }
    } catch (error) {
        showMessageModal('error', 'Error assigning region data');
        throw error;
    } finally {
        generalSettingsStore.setIsUpdatingRegionData(false);
    }
};

const updateRegionSettingsData = async () => {
    const { generalSettingsStore, regionStore } = getStore();
    const { regionName, regionExtraVoteQuantity, regionSettingsData, selectedRegionData } = generalSettingsStore;
    const regionId = regionStore.selectedRegionId;
    const regionNumber = selectedRegionData.number || '';
    const regionDescription = selectedRegionData.description || '';

    const isValidForm = !!regionName && regionExtraVoteQuantity.trim() !== '' && Number(regionExtraVoteQuantity) >= 0;
    if (!isValidForm) {
        !regionName && generalSettingsStore.setRegionNameError(true);
        (regionExtraVoteQuantity.trim() === '' || Number(regionExtraVoteQuantity) < 0) &&
            generalSettingsStore.setRegionExtraVoteQuantityError(true);
        generalSettingsStore.showRegionErrorModal();
        return;
    }

    generalSettingsStore.setIsUpdatingRegionData(true);

    const regionData = {
        name: regionName,
        number: regionNumber,
        description: regionDescription,
    };
    const data: UpdateSettingsProps[] = [];
    const dataToAssign: UpdateSettingsProps[] = [];
    if (!isEmpty(regionSettingsData)) {
        regionSettingsData?.forEach((item) => {
            const attr = {
                id: item?.id || '',
                value: item?.value || '',
                type: item?.type || '',
            };
            if (!item.isAttributeAssigned) {
                dataToAssign.push({ ...attr });
            }
            data.push({ ...attr });
        });
    }

    if (!isEmpty(dataToAssign)) {
        assignToRegionSettingsData({ regionId, data: dataToAssign });
    }

    try {
        const result = await regionApi.updateRegionSettings({ regionId, regionData, data });
        if (result.success) {
            generalSettingsStore.updateRegionAttributeData();
            updateRegions(selectedRegionData);
        }
    } catch (error) {
        showMessageModal('error', 'Error updating region data');
        throw error;
    } finally {
        generalSettingsStore.setIsUpdatingRegionData(false);
    }
};

export default updateRegionSettingsData;
